import type { ChangeEvent, EventHandler } from 'react'
import { fromJS } from 'immutable'
import { noop } from 'lodash-es'
import { useRef, useState } from 'react'
import { useSelector, useStore } from 'react-redux'

import { canvasToFile, loadImage, makeImageSquare } from '../../utils/faviconHelpers'
import { getErrorReasonMessage } from '../templateComponents/Workspace/plugins/image/ImageField'
import { imageUrl, resizedUrl, storeFile } from '../templateComponents/Workspace/daliConfig'
import { useTranslate } from '../../utils/translate'
import FormValue from '../formComponents/FormValue'
import useHelpCenterLink from '../../utils/hooks/useHelpCenterLink'

const recommendedImageWidth = 180
const recommendedImageHeight = 180

const faviconErrorsBaseKey = 'uploadFavicon.faviconField.errorMessages.reason'

type ShowFaviconProps = {
  url: string
  openFileUpload: VoidFunction
  onRemoveFavicon: VoidFunction
} & TranslateProps

function ShowFavicon({ t, url, openFileUpload, onRemoveFavicon }: Readonly<ShowFaviconProps>) {
  // The url property is a required string and even an empty string can be split().pop()'ed.
  // Try and run this in your browser or nodejs: ''.split('/').pop()
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const filename = url.split('/')!.pop()!.replace(/\?.+/, '')

  return (
    <div className="ep-custom-settings-item-upload">
      <span
        title={t('uploadFavicon.faviconField.tooltip')}
        onClick={openFileUpload}
        className="ep-custom-settings-item-upload-image"
      >
        <img alt="" src={url} />
      </span>
      <span title={filename} className="ep-custom-settings-file-name">
        {filename}
      </span>
      <button
        type="button"
        className="ep-custom-settings-item-upload-delete"
        onClick={onRemoveFavicon}
        title={t('uploadFavicon.deleteButton.label')}
      />
    </div>
  )
}

type FaviconFieldProps = {
  faviconUrl?: string
  validate: (data?: Storage.Image) => Error | undefined
  onError?: (error: Error) => void
  onChange: (newData?: Storage.Image) => void
} & TranslateProps

function FaviconField({ t, faviconUrl, validate, onChange, onError = noop }: Readonly<FaviconFieldProps>) {
  // The redux store type can not contain our api property.
  const store: any = useStore()
  const uploadRef = useRef<HTMLInputElement>(null)

  const [uploading, setIsUploading] = useState(false)
  const upload = storeFile(store.api)
  const previewUrl: (url: string) => string = resizedUrl(32)

  const validateUpload = (fileInfo: Storage.Image) => {
    const validationError = validate(fileInfo)
    if (validationError) {
      throw validationError
    }
  }

  const handleFaviconChange: EventHandler<ChangeEvent<HTMLInputElement>> = async (event) => {
    if (!event.target.files) return false
    const file = event.target.files[0]
    setIsUploading(true)

    const response = await handleUpload(file)
    if (response) {
      try {
        validateUpload(response)
        onChange(response)
      } catch (error) {
        onError(error)
      } finally {
        setIsUploading(false)
      }
    }
  }

  const handleUpload = async (file: File) => {
    try {
      // Load image and ensure that it's a square, in order to avoid
      // issues like missing favicons in Google search results.
      const image = await loadImage(file)
      if (image.width === image.height) {
        return await upload(file)
      } else {
        const squareImageCanvas = makeImageSquare(image)
        const newFile = await canvasToFile(squareImageCanvas, file.name, file.type)
        return await upload(newFile)
      }
    } catch (error) {
      const reason = getErrorReasonMessage(error, t, faviconErrorsBaseKey)
      const message = t(
        `uploadFavicon.faviconField.errorMessages.${faviconUrl ? 'messageWithOldImage' : 'messageWithoutOldImage'}`,
        { reason },
      )
      const faviconFieldError = new Error(message)
      faviconFieldError.code = error.response?.status
      onError(faviconFieldError)
    } finally {
      setIsUploading(false)
    }
  }

  const handleOpenFileUpload = () => uploadRef.current?.click()

  return (
    <fieldset disabled={uploading}>
      {faviconUrl ? (
        <ShowFavicon
          t={t}
          url={previewUrl(faviconUrl)}
          openFileUpload={handleOpenFileUpload}
          onRemoveFavicon={() => onChange()}
        />
      ) : (
        <button type="button" className="ep-custom-settings-add" onClick={handleOpenFileUpload}>
          {t('uploadFavicon.addButton.label')}
        </button>
      )}
      <input ref={uploadRef} type="file" accept="image/png" onChange={handleFaviconChange} value="" />
    </fieldset>
  )
}

export default function FaviconSettings() {
  const t = useTranslate('interface', 'components.bottomBar.storefrontSettings.faviconSettings')

  const [error, setError] = useState<Error | null>(null)
  const shopSlug = useSelector<State, string>((state) => state.getIn(['shop', 'slug']))
  const helpCenterUrl = useHelpCenterLink('IMAGE_FORMATS')
  const isUnsupportedMediaType = error?.code === 415

  const imageUrlBuilder = imageUrl(shopSlug)
  const validateFaviconSizes = (faviconFile: Storage.Image) => {
    const { width, height, mediaType } = faviconFile
    if (width < recommendedImageWidth && height < recommendedImageHeight) {
      return new Error(t('uploadFavicon.faviconField.errorMessages.reason.size'))
    }
    if (mediaType !== 'image/png') {
      const unsupportedMediaTypeError = new Error(t('uploadFavicon.faviconField.errorMessages.reason.filetype'))
      unsupportedMediaTypeError.code = 415
      return unsupportedMediaTypeError
    }
  }

  return (
    <>
      {error && (
        <div className="ep-form-row">
          <div className="dali-notification-danger">
            {error.message}
            {isUnsupportedMediaType && (
              <a
                href={helpCenterUrl}
                target="_blank"
                className="ep-form-row-text-external-link"
                rel="noreferrer noopener"
              >
                {t('components.imageUploadComponent.imageField.errorMessages.helpCenterLink')}
              </a>
            )}
          </div>
        </div>
      )}

      <div className="dali-settingslayer-element">{t('description')}</div>

      <div className="ep-form-row">
        <div className="dali-settingslayer-element">
          <div className="dali-settingslayer-hint">
            {t('imageInfo', { recommendedImageWidth, recommendedImageHeight })}
          </div>
        </div>
      </div>
      <FormValue name="faviconSettings">
        {(favicon: ImmutableMap, setFavicon: (newFavicon: ImmutableMap) => void) => {
          const handleError = (error: Error) => setError(error)

          const handleChange = (data?: Storage.Image) => {
            setError(null)
            if (data) {
              const { absoluteDownloadUrl } = data
              return setFavicon(
                fromJS({
                  faviconUrl: imageUrlBuilder(absoluteDownloadUrl),
                }),
              )
            }
            // to remove in case of empty result
            return setFavicon(fromJS({ faviconUrl: '' }))
          }

          return (
            <FaviconField
              t={t}
              faviconUrl={favicon.get('faviconUrl')}
              onChange={handleChange}
              onError={handleError}
              validate={validateFaviconSizes}
            />
          )
        }}
      </FormValue>
    </>
  )
}

FaviconSettings.displayName = 'FaviconSettings'
FaviconSettings.defaultValue = fromJS({
  faviconUrl: '',
})
